import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api-client';
import { useCurrentUser } from '../lib/hooks';
import { setTheme, useTheme, type Theme } from '../lib/theme';

/** Satu baris menu dengan ikon kiri dan chevron kanan. */
function MenuRow({
  to,
  icon,
  label,
  trailing,
  muted = false,
}: {
  to?: string;
  icon: React.ReactNode;
  label: string;
  trailing?: React.ReactNode;
  muted?: boolean;
}) {
  const body = (
    <div className="flex min-h-[52px] items-center gap-3 px-4">
      <span className="shrink-0">{icon}</span>
      <span
        className={`flex-1 text-sm font-semibold ${
          muted ? 'text-neutral-500' : 'text-neutral-200'
        }`}
      >
        {label}
      </span>
      {trailing ?? (
        <svg className="text-neutral-500" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 5l7 7-7 7" />
        </svg>
      )}
    </div>
  );
  return to ? (
    <Link to={to} className="block border-b border-line/5 last:border-0">
      {body}
    </Link>
  ) : (
    <div className="border-b border-line/5 last:border-0">{body}</div>
  );
}

/** Dua pilihan tema pada baris menu Profil. */
const THEMES: ReadonlyArray<{ value: Theme; label: string }> = [
  { value: 'dark', label: 'Gelap' },
  { value: 'light', label: 'Terang' },
];

const iconProps = {
  className: 'text-brand-500',
  width: 19,
  height: 19,
  viewBox: '0 0 24 24',
  fill: 'none',
  // currentColor + className: warna ikut token tema, bukan hex mati.
  stroke: 'currentColor',
  strokeWidth: 1.9,
  strokeLinecap: 'round' as const,
};

export function ProfilPage() {
  const meQuery = useCurrentUser();
  const me = meQuery.data?.user ?? null;
  const theme = useTheme();

  // Jumlah favorit & riwayat hanya diambil bila memang login Telegram.
  const favQuery = useQuery({
    queryKey: ['favorites'],
    queryFn: () => api.favorites(),
    enabled: me !== null,
  });
  const histQuery = useQuery({
    queryKey: ['history'],
    queryFn: () => api.history(),
    enabled: me !== null,
  });

  const displayName = me ? (me.username ?? me.first_name) : 'Tamu';

  return (
    <div className="mx-auto max-w-3xl px-4 safe-top">
      {/* Identitas */}
      <header className="flex items-center gap-4 py-4">
        {me?.photo_url ? (
          <img
            src={me.photo_url}
            alt=""
            className="h-14 w-14 shrink-0 rounded-full border border-brand-500/40 object-cover"
          />
        ) : (
          <div className="grid h-14 w-14 shrink-0 place-items-center rounded-full border border-brand-500/40 bg-gradient-to-br from-ink-800 to-ink-900 font-display text-xl font-extrabold text-brand-500">
            {displayName.slice(0, 1).toUpperCase()}
          </div>
        )}
        <div className="min-w-0">
          <h1 className="truncate font-display text-xl font-bold">{displayName}</h1>
          <p className="mt-1 flex items-center gap-1.5 text-xs text-neutral-500">
            <span className="h-1.5 w-1.5 rounded-full bg-neutral-600" />
            {me ? 'Member Gratis' : 'Belum masuk lewat Telegram'}
          </p>
        </div>
      </header>

      {/* Kartu VIP */}
      <section className="relative overflow-hidden rounded-2xl bg-gold-grad p-5 shadow-gold">
        <div className="absolute -right-6 -top-7 h-28 w-28 rounded-full bg-white/15" />
        <div className="relative flex flex-col gap-3">
          <div className="flex items-center gap-2.5">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="#14100A">
              <path d="M3 8l4.2 3.1L12 4.5l4.8 6.6L21 8l-1.7 10.2a1 1 0 0 1-1 .8H5.7a1 1 0 0 1-1-.8L3 8z" />
            </svg>
            <span className="font-display text-base font-extrabold tracking-wide text-gold-ink">
              DONGNIME VIP
            </span>
          </div>
          <p className="max-w-[16rem] text-xs font-medium leading-relaxed text-[#3D2F0E]">
            Dukung Dongnime untuk pengalaman menonton yang lebih baik.
          </p>
          <div className="flex h-11 items-center justify-center rounded-xl bg-gold-ink text-sm font-bold text-gold-light">
            Segera Hadir
          </div>
        </div>
      </section>

      {/* Menu */}
      <section className="mt-4 overflow-hidden rounded-2xl bg-ink-900">
        <MenuRow
          to="/favorites"
          label="Daftar Saya"
          icon={
            <svg {...iconProps} strokeLinejoin="round">
              <path d="M12 20.5l-1.4-1.3C5.4 14.5 2 11.4 2 7.6 2 4.9 4.1 3 6.7 3c1.6 0 3.1.8 4 2 .9-1.2 2.4-2 4-2C17.9 3 20 4.9 20 7.6c0 3.8-3.4 6.9-8.6 11.6l-1.4 1.3z" />
            </svg>
          }
          trailing={
            <span className="text-xs font-semibold text-neutral-500">
              {favQuery.data?.items.length ?? '—'}
            </span>
          }
        />
        <MenuRow
          to="/history"
          label="Riwayat Menonton"
          icon={
            <svg {...iconProps}>
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3.2 2" />
            </svg>
          }
          trailing={
            <span className="text-xs font-semibold text-neutral-500">
              {histQuery.data?.items.length ?? '—'}
            </span>
          }
        />
        <MenuRow
          label="Unduhan"
          muted
          icon={
            <svg {...iconProps} stroke="currentColor" strokeLinejoin="round" className="text-neutral-500">
              <path d="M4 15v4a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-4" />
              <path d="M12 3v12M8 11l4 4 4-4" />
            </svg>
          }
          trailing={
            <span className="rounded-md bg-brand-500/10 px-2 py-1 text-[10px] font-bold text-brand-600">
              Segera Hadir
            </span>
          }
        />
        <MenuRow
          label="Tema"
          icon={
            theme === 'dark' ? (
              <svg {...iconProps} strokeLinejoin="round">
                <path d="M20.5 14.2A8.5 8.5 0 0 1 9.8 3.5a8.5 8.5 0 1 0 10.7 10.7z" />
              </svg>
            ) : (
              <svg {...iconProps}>
                <circle cx="12" cy="12" r="4.4" />
                <path d="M12 2.6v2.2M12 19.2v2.2M4.2 12H2M22 12h-2.2M6.3 6.3L4.8 4.8M19.2 19.2l-1.5-1.5M17.7 6.3l1.5-1.5M4.8 19.2l1.5-1.5" />
              </svg>
            )
          }
          trailing={
            <div
              role="group"
              aria-label="Tema tampilan"
              className="flex overflow-hidden rounded-full border border-neutral-700"
            >
              {THEMES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setTheme(t.value)}
                  aria-pressed={theme === t.value}
                  className={`px-3 py-1.5 text-[11px] font-bold transition ${
                    theme === t.value
                      ? 'bg-gold-grad text-gold-ink'
                      : 'bg-ink-800 text-neutral-400'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          }
        />
        <MenuRow
          to="/eksplor"
          label="Jelajahi Semua Platform"
          icon={
            <svg {...iconProps}>
              <circle cx="11" cy="11" r="7.5" />
              <path d="M21 21l-4.3-4.3" />
            </svg>
          }
        />
      </section>

      {!me && (
        <p className="mt-4 rounded-xl border border-brand-500/25 bg-brand-500/5 px-4 py-3 text-xs leading-relaxed text-brand-400">
          Buka lewat bot Telegram untuk menyimpan favorit dan riwayat tontonan
          secara otomatis.
        </p>
      )}

      <p className="py-6 text-center text-[10px] tracking-[0.14em] text-neutral-700">
        DONGNIME &middot; ONE PLACE. ENDLESS STORIES.
      </p>
    </div>
  );
}
